import Color from "color";
import React, { PropsWithChildren } from "react";
import { Pressable, StyleSheet, Platform } from "react-native";
import { ColorType, useTheme } from "./AHTheme/AHTheme";
import { useBoxStyle } from "./AHTheme/useBoxStyle";
import { Box, TouchableBox } from "./AHTheme/Box";
import { AHIcon } from "./AHIcon";
import { AHText } from "./Text/AHText";

import { Circle } from "./AHTheme/Circle";
import { AHTeamIconCircleRelay } from "./AHTeamIconCircle";
import { graphql, useFragment } from "react-relay";
import { ListItemTeamIndicator_team$key } from "./__generated__/ListItemTeamIndicator_team.graphql";
import { useLayoutContext } from "./LayoutProvider";
import { SelectDot } from "./SelectIndicators";

export type ListItemProps = PropsWithChildren<{
  onPress?: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  isReorderable?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isFree?: boolean;
  disabled?: boolean;
  left?: React.ReactNode;
  leftEnabled?: boolean;

  right?: React.ReactNode;
  rightEnabled?: boolean;

  isSelected?: boolean;
  selectedBackgroundColor?: string;
  forceHeight?: number;
  minHeight?: number;
}>;

const MINIMUM_LIST_ITEM_HEIGHT = 43;

const ListItemCtx = React.createContext({
  isSelected: false,
});
export function ListItem({
  onPress,
  onPressIn,
  onPressOut,
  children,
  isFirst,
  isLast,
  isFree,
  isReorderable,
  disabled,
  left,
  leftEnabled,
  right,
  rightEnabled,
  isSelected: selected,
  selectedBackgroundColor,
  forceHeight,
  minHeight,
}: ListItemProps) {
  const theme = useTheme();

  const _style = useBoxStyle({
    spaceHorizontal: "medium",
    cornerRadiusTopLeft: isFirst || isFree ? "large" : "none",
    cornerRadiusTopRight: isFirst || isFree ? "large" : "none",
    cornerRadiusBottomLeft: isLast || isFree ? "large" : "none",
    cornerRadiusBottomRight: isLast || isFree ? "large" : "none",
  });
  const selectedBG =
    selectedBackgroundColor ??
    Color(theme["surface-primary"]).alpha(0.4).toString();
  const layout = useLayoutContext();
  return (
    <Pressable
      disabled={disabled === true || (!onPress && Platform.OS !== "web")}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={({ pressed }) => [
        _style,
        {
          flexDirection: "row",
          alignItems: "center",
          minHeight: minHeight ?? MINIMUM_LIST_ITEM_HEIGHT,
          height: isReorderable ? MINIMUM_LIST_ITEM_HEIGHT : forceHeight,
          backgroundColor: isReorderable
            ? undefined
            : pressed
              ? Color(theme.primary).lighten(0.97).toString()
              : selected
                ? selectedBG
                : theme.surface,
        },
      ]}
    >
      {left ? (
        <Box
          spaceRight="medium"
          style={{
            flexDirection: "row",
            alignItems: "center",
            opacity: leftEnabled === false ? 0 : 1,
          }}
        >
          {left}
        </Box>
      ) : null}
      <Box
        style={{
          flex: 1,
          flexDirection: "column",
          justifyContent: "center",
          paddingRight: isReorderable ? layout.space.xlarge : undefined,
        }}
        spaceVertical="medium"
      >
        <ListItemCtx.Provider
          value={{
            isSelected: selected === true,
          }}
        >
          {children}
        </ListItemCtx.Provider>
      </Box>
      {right ? (
        <Box
          spaceLeft="medium"
          style={{
            flexDirection: "row",
            alignItems: "center",
            maxWidth: "50%",
            justifyContent: "flex-end",
            opacity: rightEnabled === false ? 0 : 1,
          }}
        >
          {right}
        </Box>
      ) : null}
    </Pressable>
  );
}

function ListItemNavArrow() {
  return <AHIcon type="chevron-right" color="text-washed" size="button" />;
}
function ListItemTitle(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
}) {
  const { isSelected } = React.useContext(ListItemCtx);
  return (
    <AHText
      type={isSelected ? "list-item-title-selected" : "list-item-title"}
      color={props.color}
      numberOfLines={props.numberOfLines}
    >
      {props.children}
    </AHText>
  );
}

function ListItemSecondaryTitle(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
}) {
  const { isSelected } = React.useContext(ListItemCtx);
  return (
    <AHText
      type={
        isSelected ? "list-item-title-selected" : "list-item-title-secondary"
      }
      color={props.color}
      numberOfLines={props.numberOfLines}
    >
      {props.children}
    </AHText>
  );
}
function ListItemAddTitle(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
}) {
  return (
    <AHText
      type="list-item-title-selected"
      color="primary"
      numberOfLines={props.numberOfLines}
    >
      {props.children}
    </AHText>
  );
}
function ListItemSubTitle(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
}) {
  return (
    <AHText color={props.color} numberOfLines={props.numberOfLines}>
      {props.children}
    </AHText>
  );
}
function ListItemCheck() {
  return <AHIcon type="check" color="primary" />;
}

function ListItemDetailAction(props: { children?: React.ReactNode }) {
  return (
    <AHText selectable={false} spaceRight="xsmall" color="primary">
      {props.children}
    </AHText>
  );
}
function SelectIndicator(props: { selected: boolean; disabled?: boolean }) {
  return (
    <Circle
      size={Platform.OS === "web" ? "small" : "smallMedium"}
      color={props.selected ? "status-active" : "surface-backgroundControl"}
      borderColor={props.selected ? "status-active" : "border-strong"}
      style={{
        borderWidth: 1,
        opacity: props.disabled ? 0.5 : 1,
      }}
    />
  );
}

function ListItemTeamIndicator(props: {
  team: ListItemTeamIndicator_team$key;
}) {
  const team = useFragment(
    graphql`
      fragment ListItemTeamIndicator_team on Team {
        ...AHTeamIconCircle
      }
    `,
    props.team
  );
  return <AHTeamIconCircleRelay size="smallMedium" team={team} />;
}

function ListItemRemoveButton(props: { onPress: () => void }) {
  return (
    <TouchableBox onPress={props.onPress}>
      <Circle color="negative" size="smallMedium">
        <AHIcon type="remove" color="surface" size="small" />
      </Circle>
    </TouchableBox>
  );
}
function ListItemAddButton() {
  return <AHIcon type="add-circle" color="primary" size="smallMedium" />;
  // return (
  //   <Circle size="smallMedium" borderWidth={2} borderColor="primary">
  //     <AHIcon type="add" size="small" color="primary" />
  //   </Circle>
  // );
}
ListItem.AddButton = ListItemAddButton;
ListItem.RemoveButton = ListItemRemoveButton;
ListItem.Check = ListItemCheck;
ListItem.Title = ListItemTitle;
ListItem.TitleSecondary = ListItemSecondaryTitle;
ListItem.AddTitle = ListItemAddTitle;
ListItem.SubTitle = ListItemSubTitle;
ListItem.NavArrow = ListItemNavArrow;
ListItem.DetailActionText = ListItemDetailAction;
ListItem.SelectIndicator = SelectIndicator;
ListItem.TeamIndicator = ListItemTeamIndicator;

ListItem.MINIMUM_LIST_ITEM_HEIGHT = MINIMUM_LIST_ITEM_HEIGHT;

/*

    EXAMPLES:
<ListItem right={<ListItem.NavArrow />}>
  <ListItem.Title>Lägg till konstrollpunkter</ListItem.Title>
</ListItem>;

<ListItem left={<AHTeamIconCircle type="cash" color="#33FF66" />} right={<ListItem.NavArrow />}>
  <ListItem.Title>11111111</ListItem.Title>
</ListItem>;

<ListItem
  left={<AHTeamIconCircle type="cash" color="#33FF66" />}
  right={<ListItem.NavArrow />}
>
  <ListItem.Title>11111111</ListItem.Title>
</ListItem>


<ListItem left={<Circle color="primary" />} right={<AHButton />}>
  <ListItem.Title>11111111</ListItem.Title>
  <ListItem.SubTitle>
    <AHIcon type="link" />
    Frukt, grönt{" "}
  </ListItem.SubTitle>
</ListItem>;

*/
